import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";

/** Mirrors private/auth's usageRoutes.ts UsageResult exactly. */
interface UsageBreakdownItem {
  name: string;
  value: number;
}
interface UsageLimit {
  key: string;
  label: string;
  period: "daily" | "monthly" | "point-in-time";
  periodLabel: string;
  /** YYYY-MM-DD (UTC) — the monthly period's REAL start/end, from
   *  Cloudflare's own billing period (or a calendar-month fallback if
   *  that lookup failed server-side) — see usageRoutes.ts's
   *  resolveBillingPeriod. Only set for "monthly" limits; daily limits'
   *  window is always "since 00:00 UTC today", computed client-side. */
  periodStart?: string;
  periodEnd?: string;
  current: number;
  max: number;
  unit: string;
  items: UsageBreakdownItem[];
  error?: string;
}
interface UsageProduct {
  key: string;
  label: string;
  limits: UsageLimit[];
}
/** Mirrors private/auth's workersCpuUsage.ts WorkersCpuReport. */
interface WorkerCpuScript {
  scriptName: string;
  project: string | null;
  requests: number;
  exceededCpu: number;
  exceededCpuToday: number;
  errors: number;
  cpuP50Ms: number | null;
  cpuP99Ms: number | null;
  wallP99Ms: number | null;
  pctOverCap: number;
  statuses: Record<string, number>;
}
interface RouteStatRow {
  script: string;
  route: string;
  requests: number;
  errors: number;
  exceptions: number;
  cpuExceeded: number;
  wallAvgMs: number;
  wallP99Ms: number;
  wallMaxMs: number;
}
interface SourceRow {
  name: string;
  requests: number;
  errors: number;
  exceededCpu: number;
  exceededCpuToday: number;
  statuses: Record<string, number>;
  activeSeconds?: number;
}
interface SourceReport {
  key: string;
  label: string;
  dataset: string;
  error?: string;
  rows: SourceRow[];
}
interface WorkersCpuReport {
  sources: SourceReport[];
  allHits: {
    total24h: number;
    today: number;
    worst: { name: string; count: number; source: string } | null;
  };
  notes: string[];
  capMs: number;
  windowLabel: string;
  scripts: WorkerCpuScript[];
  totalExceeded24h: number;
  totalExceededToday: number;
  totalErrors24h: number;
  worst: { scriptName: string; count: number } | null;
  error?: string;
  quantilesError?: string;
  routes: {
    available: boolean;
    note: string;
    rows: RouteStatRow[];
    error?: string;
    coverage: Array<{
      scriptName: string;
      project: string | null;
      instrumented: boolean;
      requests: number;
    }>;
  };
}
interface UsageResult {
  generated_at: string;
  products: UsageProduct[];
  workersCpu?: WorkersCpuReport;
}

const CPU_DRILLDOWN_ID = "worker-cpu-drilldown";
const CPU_ROW_KEYS = new Set([
  "workers_cpu_exceeded",
  "workers_errors",
  "workers_cpu_p99",
]);

function scrollToCpuDrilldown() {
  const el = document.getElementById(CPU_DRILLDOWN_ID);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  el.focus({ preventScroll: true });
}

function fmtMs(n: number | null): string {
  return n === null ? "—" : `${fmt(n)} ms`;
}

/** Red/amber banner shown at the top of the page when anything was killed for exceeding CPU. */
function CpuHitsBanner({ report }: { report: WorkersCpuReport }) {
  const hits = report.allHits;
  if (!hits.worst) return null;
  const severe = hits.total24h >= 100;
  return (
    <Alert variant={severe ? "destructive" : "warning"}>
      <AlertTitle>
        {hits.total24h} CPU-limit hit{hits.total24h === 1 ? "" : "s"} in the
        last 24 h — worst: <span className="font-mono">{hits.worst.name}</span>{" "}
        ({hits.worst.count}, {hits.worst.source})
      </AlertTitle>
      <AlertDescription>
        Free plan allows {report.capMs} ms CPU per request. {hits.today} since
        00:00 UTC today. Counts Workers, Durable Objects and Pages Functions.{" "}
        <button
          type="button"
          onClick={scrollToCpuDrilldown}
          className="underline font-medium"
        >
          See which Worker and route →
        </button>
      </AlertDescription>
    </Alert>
  );
}

/** Non-"success" outcomes, worst first: the actual failing status even when it is not exceededCpu. */
function failingStatuses(statuses: Record<string, number>): string {
  const parts = Object.entries(statuses)
    .filter(([k, v]) => k !== "success" && v > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${k} ${fmt(v)}`);
  return parts.length ? parts.join(", ") : "—";
}

/** One table per non-Worker compute source; its own error never hides the others. */
function SourceTable({ source }: { source: SourceReport }) {
  return (
    <div>
      <h3 className="text-sm font-semibold">
        {source.label}{" "}
        <span className="font-mono text-xs font-normal text-muted-foreground">
          {source.dataset}
        </span>
      </h3>
      {source.error ? (
        <p className="text-xs text-destructive" role="alert">
          Unavailable — {source.error}
        </p>
      ) : source.rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No invocations in this window.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <caption className="sr-only">{source.label}</caption>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                {source.key === "do_duration" ? (
                  <TableHead className="text-right">Active (24 h)</TableHead>
                ) : (
                  <>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">CPU exceeded</TableHead>
                    <TableHead className="text-right">Errors</TableHead>
                    <TableHead>Non-success outcomes</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {source.rows.map((r) => (
                <TableRow
                  key={r.name}
                  className={cn(r.exceededCpu > 0 && "bg-destructive/10")}
                >
                  <TableCell className="font-mono text-xs">{r.name}</TableCell>
                  {source.key === "do_duration" ? (
                    <TableCell className="text-right">
                      {fmt(r.activeSeconds ?? 0)} s
                    </TableCell>
                  ) : (
                    <>
                      <TableCell className="text-right">
                        {fmt(r.requests)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right",
                          r.exceededCpu > 0 && "text-destructive font-medium",
                        )}
                      >
                        {fmt(r.exceededCpu)}
                        <span className="text-xs text-muted-foreground">
                          {" "}
                          ({fmt(r.exceededCpuToday)} today)
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {fmt(r.errors)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {failingStatuses(r.statuses)}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/** Per-Worker exact table + per-route wall-time ESTIMATE table. */
function CpuDrilldown({ report }: { report: WorkersCpuReport }) {
  const worstName = report.worst?.scriptName;
  return (
    <Card id={CPU_DRILLDOWN_ID} tabIndex={-1} className="scroll-mt-4">
      <CardHeader>
        <CardTitle>Workers CPU-limit hits</CardTitle>
        <CardDescription>
          Exact numbers from Cloudflare analytics (workersInvocationsAdaptive),{" "}
          {report.windowLabel}. Cap: {report.capMs} ms CPU per request. See the
          Worker CPU audit plan (
          <span className="font-mono">
            internal/compiler/plans/2026-10-16-worker-cpu-audit.md
          </span>
          ) for the fixes under way.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {report.error ? (
          <p className="text-sm text-destructive" role="alert">
            Unavailable — {report.error}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <caption className="sr-only">
                Per-Worker CPU limit hits, sorted by exceeded-CPU count
              </caption>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead className="text-right">Requests</TableHead>
                  <TableHead className="text-right">CPU exceeded</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead className="text-right">CPU p50</TableHead>
                  <TableHead className="text-right">CPU p99</TableHead>
                  <TableHead className="text-right">% killed</TableHead>
                  <TableHead>Non-success outcomes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.scripts.map((s) => {
                  const worst = s.scriptName === worstName;
                  return (
                    <TableRow
                      key={s.scriptName}
                      className={cn(worst && "bg-destructive/10")}
                    >
                      <TableCell className="font-mono">
                        {s.scriptName}
                        {worst && (
                          <Badge variant="destructive" className="ml-2">
                            worst offender
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {s.project ?? "(unmapped)"}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmt(s.requests)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right font-medium",
                          s.exceededCpu > 0 && "text-destructive",
                        )}
                      >
                        {fmt(s.exceededCpu)}
                        <span className="text-xs text-muted-foreground">
                          {" "}
                          ({fmt(s.exceededCpuToday)} today)
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {fmt(s.errors)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmtMs(s.cpuP50Ms)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right",
                          s.cpuP99Ms !== null &&
                            s.cpuP99Ms > report.capMs &&
                            "text-destructive font-medium",
                        )}
                      >
                        {fmtMs(s.cpuP99Ms)}
                      </TableCell>
                      <TableCell className="text-right">
                        {s.pctOverCap.toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-xs">
                        {failingStatuses(s.statuses)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {report.scripts.length === 0 && (
              <p className="text-sm text-muted-foreground py-2">
                No Worker invocations in this window.
              </p>
            )}
            {report.quantilesError && (
              <p className="text-xs text-destructive pt-2" role="alert">
                CPU quantiles unavailable — {report.quantilesError}
              </p>
            )}
          </div>
        )}

        {report.sources.map((src) => (
          <SourceTable key={src.key} source={src} />
        ))}

        <div>
          <h3 className="text-sm font-semibold">What the dashboard may hide</h3>
          <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1">
            {report.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-semibold">
            Top routes by p99 wall time (estimate of CPU hogs)
          </h3>
          <p className="text-xs text-muted-foreground mb-2">
            {report.routes.note}
          </p>
          <ul className="text-xs space-y-0.5 mb-2">
            {report.routes.coverage.map((c) => (
              <li key={c.scriptName}>
                <span className="font-mono">{c.scriptName}</span>{" "}
                <span className="text-muted-foreground">
                  ({c.project ?? "unmapped"})
                </span>{" "}
                {c.instrumented ? (
                  <span>{fmt(c.requests)} requests instrumented</span>
                ) : (
                  <Badge variant="secondary">not instrumented yet</Badge>
                )}
              </li>
            ))}
          </ul>
          {report.routes.error ? (
            <p className="text-xs text-destructive" role="alert">
              Route stats unavailable — {report.routes.error}
            </p>
          ) : report.routes.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No route data yet — instrumented Workers flush at most once a
              minute after deploy.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <caption className="sr-only">
                  Routes ranked by caught CPU-exceeded errors then wall-time p99
                  (estimate)
                </caption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Worker</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">
                      CPU-exceeded (caught)
                    </TableHead>
                    <TableHead className="text-right">Exceptions</TableHead>
                    <TableHead className="text-right">Wall avg</TableHead>
                    <TableHead className="text-right">Wall p99 ≈</TableHead>
                    <TableHead className="text-right">Wall max</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.routes.rows.map((r) => (
                    <TableRow key={`${r.script}\0${r.route}`}>
                      <TableCell className="font-mono text-xs">
                        {r.script}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {r.route}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmt(r.requests)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right",
                          r.cpuExceeded > 0 && "text-destructive font-medium",
                        )}
                      >
                        {fmt(r.cpuExceeded)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmt(r.exceptions)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmtMs(r.wallAvgMs)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmtMs(r.wallP99Ms)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmtMs(r.wallMaxMs)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function errMsg(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

const numberFmt = new Intl.NumberFormat();

function fmt(n: number): string {
  return numberFmt.format(Math.round(n * 1000) / 1000);
}

function pctOf(limit: UsageLimit): number {
  if (limit.max <= 0) return 0;
  return (limit.current / limit.max) * 100;
}

/** green under 80%, yellow 80-100%, red over 100% — same thresholds the
 * old single-metric page used for its one banner, now driving every row. */
function barColor(pct: number): string {
  if (pct > 100) return "bg-destructive";
  if (pct > 80) return "bg-yellow-500";
  return "bg-green-600";
}

/** How far through its OWN reset window we are right now, as a 0-100
 * position for the "you are here" caret below the bar — entirely a
 * client-side clock computation, not a server round-trip: for "daily"
 * this is always UTC midnight-to-midnight, computed here directly. For
 * "monthly" this uses periodStart/periodEnd — NOT an assumed calendar
 * month — since this account's real billing period isn't the 1st-to-1st
 * (confirmed live, 2026-08-27: Cloudflare's own dashboard showed
 * Aug 12 – Sep 12 while this used to compute Aug 1 – Aug 31, putting the
 * caret in the wrong place entirely). Purely a visual reference ("half
 * the period is gone and we're already at 90%") — not a projection or a
 * claim about what usage WILL be by period end. Null for "point-in-time"
 * limits (storage etc.), which have no period to be partway through, or
 * for a "monthly" limit missing periodStart/periodEnd (an older cached
 * response, or the row itself errored before those were ever set). */
function periodElapsedPct(limit: UsageLimit): number | null {
  const now = new Date();
  if (limit.period === "daily") {
    const msIntoDay =
      now.getTime() -
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return (msIntoDay / (24 * 60 * 60 * 1000)) * 100;
  }
  if (limit.period === "monthly" && limit.periodStart && limit.periodEnd) {
    const start = new Date(`${limit.periodStart}T00:00:00Z`).getTime();
    const end = new Date(`${limit.periodEnd}T00:00:00Z`).getTime();
    if (end <= start) return null; // malformed — don't render a nonsensical caret
    return ((now.getTime() - start) / (end - start)) * 100;
  }
  return null;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
}

interface R2ObjectEntry {
  key: string;
  size: number;
  uploaded: string | null;
  // Only ever set for the pure-jobs bucket (server-side enrichment —
  // see private/auth's r2BrowseRoutes.ts enrichPureJobsObjects), when the
  // key's job-id prefix matches a real job row.
  job_id?: string;
  job_status?: string;
  job_entry?: string;
  org_id?: string;
  org_name?: string | null;
  user_id?: string | null;
  user_name?: string | null;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

interface ImageRegistryTag {
  reference: string;
  digest: string;
  media_type: string;
  size: number;
  created_at: string;
}
interface ImageRegistryRepo {
  name: string;
  tags: ImageRegistryTag[];
}

/**
 * pure-image-registry-specific view (2026-08-27) — the generic flat
 * object list is useless here: raw keys are content-addressed blobs
 * (`blobs/sha256/<digest>`, not namespaced by image) and manifests keyed
 * by digest, not tag — no "grouped by image, newest first" structure
 * exists in the R2 keys themselves. Reads the registry's own D1
 * `manifests` table instead (server-side — see r2BrowseRoutes.ts's
 * handleListImageRegistryImages), which already has exactly the
 * repo/tag/digest/size/created_at grouping an operator actually wants.
 *
 * Deletes a TAG (its manifest, D1 row + R2 object) — NOT the underlying
 * layer blobs, which can be shared across images with no reference
 * tracking anywhere in this codebase (see backend header comment for
 * the full reasoning) — so this alone won't reclaim all the space an
 * old image "should" free. Said explicitly in the UI, not left implicit.
 */
/** Shared by every collapsible-section dialog below (repos, packages,
 * artifact crates) — toggles membership of `key` in a Set<string> of
 * currently-expanded section names. */
function toggleExpanded(
  set: Dispatch<SetStateAction<Set<string>>>,
  key: string,
) {
  set((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
}

function ImageRegistryBrowserDialog({
  open,
  onOpenChange,
  call,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  call: <T>(path: string, init?: RequestInit) => Promise<T>;
}) {
  const [repos, setRepos] = useState<ImageRegistryRepo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null); // `${name}\0${reference}`
  // Default collapsed, same as the registry-internal dialog — consistent
  // pattern once a repo has more than a couple tags to scroll through.
  const [expandedRepos, setExpandedRepos] = useState<Set<string>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    call<{ ok: true; repositories: ImageRegistryRepo[] }>(
      "/admin/r2/pure-image-registry/images",
    )
      .then((r) => setRepos(r.repositories))
      .catch((err) => setError(errMsg(err, "failed to load images")))
      .finally(() => setLoading(false));
  }, [call]);

  useEffect(() => {
    if (!open) return;
    setRepos(null);
    load();
  }, [open, load]);

  const deleteTag = (name: string, reference: string) => {
    if (
      !window.confirm(
        `Delete tag "${name}:${reference}"? This removes the manifest only — shared layer blobs are NOT deleted. Cannot be undone.`,
      )
    )
      return;
    const id = `${name}\0${reference}`;
    setDeleting(id);
    const params = new URLSearchParams({ name, reference });
    call<{ ok: true }>(`/admin/r2/pure-image-registry/images?${params}`, {
      method: "DELETE",
    })
      .then(() =>
        setRepos(
          (prev) =>
            prev
              ?.map((r) =>
                r.name === name
                  ? {
                      ...r,
                      tags: r.tags.filter((t) => t.reference !== reference),
                    }
                  : r,
              )
              .filter((r) => r.tags.length > 0) ?? null,
        ),
      )
      .catch((err) => setError(errMsg(err, "failed to delete tag")))
      .finally(() => setDeleting(null));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-mono">pure-image-registry</DialogTitle>
          <DialogDescription>
            Grouped by repository, tags newest-created first. Deleting a tag
            removes its manifest only — shared layer blobs aren't reclaimed (no
            reference tracking exists to do that safely yet).
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {loading && !repos && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}

        <div className="max-h-[32rem] overflow-y-auto space-y-4">
          {repos?.map((repo) => {
            const isOpen = expandedRepos.has(repo.name);
            const totalBytes = repo.tags.reduce((sum, t) => sum + t.size, 0);
            return (
              <div key={repo.name}>
                <button
                  type="button"
                  onClick={() => toggleExpanded(setExpandedRepos, repo.name)}
                  className="w-full flex items-center justify-between gap-2 text-left mb-1"
                >
                  <span className="text-sm font-semibold font-mono">
                    {isOpen ? "▾" : "▸"} {repo.name}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {repo.tags.length} tag{repo.tags.length === 1 ? "" : "s"} ·{" "}
                    {fmtBytes(totalBytes)}
                  </span>
                </button>
                {isOpen && (
                  <div className="border rounded-md">
                    {repo.tags.map((tag, i) => (
                      <div
                        key={tag.reference}
                        className="flex items-center justify-between gap-2 px-3 py-2 text-xs border-b last:border-b-0"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono">{tag.reference}</span>
                            {i === 0 && (
                              <Badge variant="secondary">most recent</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-3 text-muted-foreground mt-0.5">
                            <span>{fmtBytes(tag.size)}</span>
                            <span>{fmtDate(tag.created_at)}</span>
                            <span
                              className="font-mono truncate max-w-[14rem]"
                              title={tag.digest}
                            >
                              {tag.digest}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={
                            deleting === `${repo.name}\0${tag.reference}`
                          }
                          onClick={() => deleteTag(repo.name, tag.reference)}
                          className="shrink-0"
                        >
                          {deleting === `${repo.name}\0${tag.reference}`
                            ? "deleting…"
                            : "Delete"}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {repos?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No images in this registry.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface RegistryInternalVersion {
  version: string;
  publishedAt: string | null;
  size: number | null;
  distTags: string[];
}
interface RegistryInternalPackage {
  name: string;
  versions: RegistryInternalVersion[];
}
interface RegistryInternalArtifactVersion {
  version: string;
  size: number;
  uploaded: string | null;
  isLatest: boolean;
}
interface RegistryInternalCrate {
  crate: string;
  versions: RegistryInternalArtifactVersion[];
}
interface UnaccountedObject {
  key: string;
  size: number;
  uploaded: string | null;
}
interface RegistryInternalPackagesResponse {
  ok: true;
  packages: RegistryInternalPackage[];
  artifactCrates: RegistryInternalCrate[];
  unaccountedObjects: UnaccountedObject[];
}

/**
 * pure-registry-internal-specific view (2026-08-27) — this npm registry
 * deliberately has NO D1 database (source-of-truth lives entirely in R2
 * packument JSON — see private/auth's r2BrowseRoutes.ts own header), so
 * unlike pure-image-registry the grouped view here is reconstructed
 * server-side by reading each package's packument rather than queried
 * from a table. Versions sorted newest-published first; a version
 * carrying a dist-tag (e.g. "latest") is badged with it.
 *
 * FIXED 2026-09-02: this bucket also holds pure_interp's generic
 * artifact tarballs (artifacts/<crate>/<version>.tar.gz) — a completely
 * separate namespace from npm packuments/tarballs, which this dialog
 * never showed at all. That's why the totals didn't add up (~2GB the
 * operator couldn't see or manage). Now shows both, plus an explicit
 * warning if the backend's own cross-check against a full bucket
 * listing still finds bytes neither section accounts for.
 */
function RegistryInternalBrowserDialog({
  open,
  onOpenChange,
  call,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  call: <T>(path: string, init?: RequestInit) => Promise<T>;
}) {
  const [packages, setPackages] = useState<RegistryInternalPackage[] | null>(
    null,
  );
  const [artifactCrates, setArtifactCrates] = useState<
    RegistryInternalCrate[] | null
  >(null);
  const [unaccounted, setUnaccounted] = useState<UnaccountedObject[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null); // `${name}\0${version}`
  const [deletingArtifact, setDeletingArtifact] = useState<string | null>(null); // `${crate}\0${version}`
  const [deletingUnaccounted, setDeletingUnaccounted] = useState<string | null>(
    null,
  ); // key
  const [pruning, setPruning] = useState(false);
  const [pruneResult, setPruneResult] = useState<string | null>(null);
  // Both sections default COLLAPSED — this bucket routinely has 20+
  // versions per package/crate (see the 2026-09-02 report this whole
  // view came from), and showing all of them expanded by default made
  // deciding what to delete harder, not easier. Summary metrics in each
  // header (count, total size) are enough to decide what to expand.
  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(
    new Set(),
  );
  const [expandedCrates, setExpandedCrates] = useState<Set<string>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    call<RegistryInternalPackagesResponse>(
      "/admin/r2/pure-registry-internal/packages",
    )
      .then((r) => {
        setPackages(r.packages);
        setArtifactCrates(r.artifactCrates);
        setUnaccounted(r.unaccountedObjects);
      })
      .catch((err) => setError(errMsg(err, "failed to load packages")))
      .finally(() => setLoading(false));
  }, [call]);

  useEffect(() => {
    if (!open) return;
    setPackages(null);
    setArtifactCrates(null);
    setUnaccounted(null);
    load();
  }, [open, load]);

  const deleteVersion = (name: string, version: string) => {
    if (
      !window.confirm(
        `Delete "${name}@${version}"? This removes the tarball and its packument entry. Cannot be undone.`,
      )
    )
      return;
    const id = `${name}\0${version}`;
    setDeleting(id);
    const params = new URLSearchParams({ name, version });
    call<{ ok: true }>(`/admin/r2/pure-registry-internal/packages?${params}`, {
      method: "DELETE",
    })
      .then(() =>
        setPackages(
          (prev) =>
            prev
              ?.map((p) =>
                p.name === name
                  ? {
                      ...p,
                      versions: p.versions.filter((v) => v.version !== version),
                    }
                  : p,
              )
              .filter((p) => p.versions.length > 0) ?? null,
        ),
      )
      .catch((err) => setError(errMsg(err, "failed to delete version")))
      .finally(() => setDeleting(null));
  };

  // Added 2026-09-02 — artifacts (pure_interp, pure-compiler-vendor) had
  // no delete path at all before this; only npm package versions could
  // be deleted. Backend refuses (409) deleting whichever version the
  // latest/edge pointer currently resolves to.
  const deleteArtifactVersion = (crate: string, version: string) => {
    if (!window.confirm(`Delete "${crate}@${version}"? Cannot be undone.`))
      return;
    const id = `${crate}\0${version}`;
    setDeletingArtifact(id);
    const params = new URLSearchParams({ crate, version });
    call<{ ok: true }>(`/admin/r2/pure-registry-internal/artifacts?${params}`, {
      method: "DELETE",
    })
      .then(() =>
        setArtifactCrates(
          (prev) =>
            prev
              ?.map((c) =>
                c.crate === crate
                  ? {
                      ...c,
                      versions: c.versions.filter((v) => v.version !== version),
                    }
                  : c,
              )
              .filter((c) => c.versions.length > 0) ?? null,
        ),
      )
      .catch((err) =>
        setError(errMsg(err, "failed to delete artifact version")),
      )
      .finally(() => setDeletingArtifact(null));
  };

  // Deletes via the generic bucket-object route — an unaccounted object
  // is, definitionally, one this view can't interpret as a package or
  // artifact version, so there's no specialized delete for it to call.
  const deleteUnaccounted = (key: string) => {
    if (!window.confirm(`Delete "${key}"? Cannot be undone.`)) return;
    setDeletingUnaccounted(key);
    call<{ ok: true }>(`/admin/r2/pure-registry-internal/objects`, {
      method: "DELETE",
      body: JSON.stringify({ key }),
    })
      .then(() =>
        setUnaccounted((prev) => prev?.filter((o) => o.key !== key) ?? null),
      )
      .catch((err) => setError(errMsg(err, "failed to delete object")))
      .finally(() => setDeletingUnaccounted(null));
  };

  // Same GC pass the daily cron in pure-registry-internal itself runs
  // automatically (see that repo's src/prune.ts) — keeps the newest 3
  // dev builds per package/crate and never touches a tagged release or
  // whatever a dist-tag/edge/latest pointer currently resolves to.
  const pruneNow = () => {
    if (
      !window.confirm(
        "Delete old dev builds now? Keeps the newest 3 per package/crate, tagged releases, and anything a dist-tag or edge/latest pointer currently resolves to. Cannot be undone.",
      )
    )
      return;
    setPruning(true);
    setPruneResult(null);
    call<{
      ok: true;
      npmVersionsDeleted: unknown[];
      artifactVersionsDeleted: unknown[];
    }>("/admin/r2/pure-registry-internal/prune", { method: "POST" })
      .then((r) => {
        setPruneResult(
          `Deleted ${r.npmVersionsDeleted.length} npm version(s), ${r.artifactVersionsDeleted.length} artifact version(s).`,
        );
        load();
      })
      .catch((err) => setError(errMsg(err, "failed to prune")))
      .finally(() => setPruning(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="font-mono">
              pure-registry-internal
            </DialogTitle>
            <Button
              variant="outline"
              size="sm"
              disabled={pruning}
              onClick={pruneNow}
            >
              {pruning ? "pruning…" : "Prune old dev builds"}
            </Button>
          </div>
          {pruneResult && (
            <p className="text-xs text-muted-foreground">{pruneResult}</p>
          )}
          <DialogDescription>
            Grouped by package, versions newest-published first. Deleting a
            version removes its tarball and packument entry only.
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {loading && !packages && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}

        <div className="max-h-[32rem] overflow-y-auto space-y-4">
          {packages?.map((pkg) => {
            const isOpen = expandedPackages.has(pkg.name);
            const totalBytes = pkg.versions.reduce(
              (sum, v) => sum + (v.size ?? 0),
              0,
            );
            return (
              <div key={pkg.name}>
                <button
                  type="button"
                  onClick={() => toggleExpanded(setExpandedPackages, pkg.name)}
                  className="w-full flex items-center justify-between gap-2 text-left mb-1"
                >
                  <span className="text-sm font-semibold font-mono">
                    {isOpen ? "▾" : "▸"} {pkg.name}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {pkg.versions.length} version
                    {pkg.versions.length === 1 ? "" : "s"} ·{" "}
                    {fmtBytes(totalBytes)}
                  </span>
                </button>
                {isOpen && (
                  <div className="border rounded-md">
                    {pkg.versions.map((v, i) => (
                      <div
                        key={v.version}
                        className="flex items-center justify-between gap-2 px-3 py-2 text-xs border-b last:border-b-0"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono">{v.version}</span>
                            {i === 0 && v.publishedAt && (
                              <Badge variant="secondary">most recent</Badge>
                            )}
                            {v.distTags.map((tag) => (
                              <Badge key={tag} variant="outline">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <div className="flex flex-wrap gap-x-3 text-muted-foreground mt-0.5">
                            <span>
                              {v.size !== null ? fmtBytes(v.size) : "—"}
                            </span>
                            <span>{fmtDate(v.publishedAt)}</span>
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={deleting === `${pkg.name}\0${v.version}`}
                          onClick={() => deleteVersion(pkg.name, v.version)}
                          className="shrink-0"
                        >
                          {deleting === `${pkg.name}\0${v.version}`
                            ? "deleting…"
                            : "Delete"}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {packages?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No packages in this registry.
            </p>
          )}

          {artifactCrates && artifactCrates.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Generic artifacts (pure_interp, pure-compiler-vendor — not npm
                packages)
              </div>
              {artifactCrates.map((c) => {
                const isOpen = expandedCrates.has(c.crate);
                const totalBytes = c.versions.reduce(
                  (sum, v) => sum + v.size,
                  0,
                );
                return (
                  <div key={c.crate} className="mb-3">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(setExpandedCrates, c.crate)}
                      className="w-full flex items-center justify-between gap-2 text-left mb-1"
                    >
                      <span className="text-sm font-semibold font-mono">
                        {isOpen ? "▾" : "▸"} {c.crate}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {c.versions.length} version
                        {c.versions.length === 1 ? "" : "s"} ·{" "}
                        {fmtBytes(totalBytes)}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="border rounded-md">
                        {c.versions.map((v) => (
                          <div
                            key={v.version}
                            className="flex items-center justify-between gap-2 px-3 py-2 text-xs border-b last:border-b-0"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono">{v.version}</span>
                                {v.isLatest && (
                                  <Badge variant="outline">latest</Badge>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-x-3 text-muted-foreground mt-0.5">
                                <span>{fmtBytes(v.size)}</span>
                                <span>{fmtDate(v.uploaded)}</span>
                              </div>
                            </div>
                            <Button
                              variant="destructive"
                              size="sm"
                              disabled={
                                deletingArtifact === `${c.crate}\0${v.version}`
                              }
                              onClick={() =>
                                deleteArtifactVersion(c.crate, v.version)
                              }
                              className="shrink-0"
                            >
                              {deletingArtifact === `${c.crate}\0${v.version}`
                                ? "deleting…"
                                : "Delete"}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {unaccounted && unaccounted.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-yellow-600 dark:text-yellow-500 uppercase tracking-wide mb-2">
                Unrecognized objects (
                {fmtBytes(unaccounted.reduce((sum, o) => sum + o.size, 0))}{" "}
                across {unaccounted.length} object
                {unaccounted.length === 1 ? "" : "s"}) — keys under neither
                packuments/tarballs nor artifacts/
              </div>
              <div className="border rounded-md">
                {unaccounted.map((o) => (
                  <div
                    key={o.key}
                    className="flex items-center justify-between gap-2 px-3 py-2 text-xs border-b last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-mono truncate" title={o.key}>
                        {o.key}
                      </div>
                      <div className="flex flex-wrap gap-x-3 text-muted-foreground mt-0.5">
                        <span>{fmtBytes(o.size)}</span>
                        <span>{fmtDate(o.uploaded)}</span>
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={deletingUnaccounted === o.key}
                      onClick={() => deleteUnaccounted(o.key)}
                      className="shrink-0"
                    >
                      {deletingUnaccounted === o.key ? "deleting…" : "Delete"}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Drill into one R2 bucket's actual contents (not just its aggregate
 * size from the GraphQL-analytics breakdown) and delete individual
 * objects — opened by clicking a bucket row under the R2 "Storage"
 * limit. Own fetch/state, entirely separate from the usage-summary
 * polling above; only loads once opened. */
function BucketBrowserDialog({
  bucket,
  onOpenChange,
  call,
}: {
  bucket: string | null;
  onOpenChange: (open: boolean) => void;
  call: <T>(path: string, init?: RequestInit) => Promise<T>;
}) {
  const [objects, setObjects] = useState<R2ObjectEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  // R2's ListObjectsV2 always returns key-lexicographic order — pure-jobs
  // keys are job-id-prefixed, so that's effectively random with respect to
  // time. Default to newest-first (2026-08-27 ask) since "what's safe to
  // delete" is almost always a recency question; sorts only what's loaded
  // so far — "Load more" pages in key order, sort re-applies to the union.
  const [sortBy, setSortBy] = useState<
    "time-desc" | "time-asc" | "key-asc" | "size-desc"
  >("time-desc");
  const sortedObjects = useMemo(() => {
    const arr = [...objects];
    switch (sortBy) {
      case "time-desc":
        return arr.sort(
          (a, b) =>
            (b.uploaded ? Date.parse(b.uploaded) : 0) -
            (a.uploaded ? Date.parse(a.uploaded) : 0),
        );
      case "time-asc":
        return arr.sort(
          (a, b) =>
            (a.uploaded ? Date.parse(a.uploaded) : 0) -
            (b.uploaded ? Date.parse(b.uploaded) : 0),
        );
      case "size-desc":
        return arr.sort((a, b) => b.size - a.size);
      case "key-asc":
        return arr.sort((a, b) => a.key.localeCompare(b.key));
    }
  }, [objects, sortBy]);

  const loadPage = useCallback(
    (after: string | null, replace: boolean) => {
      if (!bucket) return;
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (after) params.set("cursor", after);
      call<{
        ok: true;
        objects: R2ObjectEntry[];
        truncated: boolean;
        cursor: string | null;
      }>(`/admin/r2/${encodeURIComponent(bucket)}/objects?${params}`)
        .then((r) => {
          setObjects((prev) => (replace ? r.objects : [...prev, ...r.objects]));
          setTruncated(r.truncated);
          setCursor(r.cursor);
        })
        .catch((err) => setError(errMsg(err, "failed to list objects")))
        .finally(() => setLoading(false));
    },
    [bucket, call],
  );

  useEffect(() => {
    if (!bucket) return;
    setObjects([]);
    setCursor(null);
    setTruncated(false);
    loadPage(null, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on bucket change, loadPage is stable enough via bucket dep
  }, [bucket]);

  const deleteObject = (key: string) => {
    if (!bucket) return;
    if (
      !window.confirm(`Delete "${key}" from ${bucket}? This cannot be undone.`)
    )
      return;
    setDeletingKey(key);
    call<{ ok: true }>(`/admin/r2/${encodeURIComponent(bucket)}/objects`, {
      method: "DELETE",
      body: JSON.stringify({ key }),
    })
      .then(() => setObjects((prev) => prev.filter((o) => o.key !== key)))
      .catch((err) => setError(errMsg(err, "failed to delete object")))
      .finally(() => setDeletingKey(null));
  };

  return (
    <Dialog open={bucket !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-mono">{bucket}</DialogTitle>
          <DialogDescription>
            Objects in this bucket — delete individually. Listed 200 at a time.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-end gap-2">
          <label htmlFor="r2-sort" className="text-xs text-muted-foreground">
            Sort
          </label>
          <select
            id="r2-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="text-xs border rounded-md px-2 py-1 bg-background"
          >
            <option value="time-desc">Newest first</option>
            <option value="time-asc">Oldest first</option>
            <option value="size-desc">Largest first</option>
            <option value="key-asc">Key (A–Z)</option>
          </select>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="max-h-[28rem] overflow-y-auto space-y-1 border rounded-md">
          {sortedObjects.map((o) => (
            <div
              key={o.key}
              className="flex items-start justify-between gap-2 px-3 py-2 text-xs border-b last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <div className="font-mono truncate" title={o.key}>
                  {o.key}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-muted-foreground mt-0.5">
                  <span>{fmtBytes(o.size)}</span>
                  <span>{fmtDate(o.uploaded)}</span>
                  {o.job_id && (
                    <>
                      <a
                        href={`/logs/${encodeURIComponent(o.job_id)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        job {o.job_id.slice(0, 8)}… ({o.job_status})
                      </a>
                      <span
                        className="truncate max-w-[10rem]"
                        title={o.job_entry}
                      >
                        {o.job_entry}
                      </span>
                      <span>
                        org: {o.org_name ?? o.org_id}
                        {o.org_name && (
                          <span className="font-mono"> ({o.org_id})</span>
                        )}
                      </span>
                      <span>
                        user:{" "}
                        {o.user_name ?? (o.user_id ? o.user_id : "unknown")}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <Button
                variant="destructive"
                size="sm"
                disabled={deletingKey === o.key}
                onClick={() => deleteObject(o.key)}
                className="shrink-0"
              >
                {deletingKey === o.key ? "deleting…" : "Delete"}
              </Button>
            </div>
          ))}
          {sortedObjects.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground p-3">
              {error ? "" : "This bucket is empty."}
            </p>
          )}
        </div>

        {truncated && (
          <Button
            variant="outline"
            disabled={loading}
            onClick={() => loadPage(cursor, false)}
          >
            {loading ? "loading…" : "Load more"}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LimitRow({
  limit,
  onOpenBucket,
}: {
  limit: UsageLimit;
  onOpenBucket?: (bucket: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const pct = pctOf(limit);
  const elapsedPct = periodElapsedPct(limit);

  return (
    <div className="border rounded-md">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex flex-col gap-1.5 p-3 text-left hover:bg-accent/40 transition-colors"
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium">{limit.label}</span>
          <span className="text-xs text-muted-foreground">
            {limit.periodLabel}
          </span>
        </div>

        {limit.error ? (
          <p className="text-xs text-destructive">
            Unavailable — {limit.error}
          </p>
        ) : limit.max <= 0 ? (
          // No Cloudflare cap for this row (a count to watch, not a quota): show the value, no bar.
          <p
            className={cn(
              "text-sm font-medium",
              limit.current > 0 ? "text-destructive" : "text-muted-foreground",
            )}
          >
            {fmt(limit.current)} {limit.unit}
          </p>
        ) : (
          <>
            <div className="relative pt-1.5">
              {elapsedPct !== null && (
                <div
                  className="absolute top-0 h-0 w-0 border-x-4 border-t-4 border-x-transparent border-t-muted-foreground/60"
                  style={{
                    left: `calc(${Math.min(100, Math.max(0, elapsedPct))}% - 4px)`,
                  }}
                  title={`${Math.round(elapsedPct)}% through the current ${limit.period === "daily" ? "day" : "month"} (UTC)`}
                />
              )}
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className={cn("h-full transition-all", barColor(pct))}
                  style={{
                    width: `${Math.min(100, Math.max(pct > 0 ? 2 : 0, pct))}%`,
                  }}
                />
              </div>
            </div>
            <div className="flex items-baseline justify-between text-xs text-muted-foreground">
              <span>
                {fmt(limit.current)} / {fmt(limit.max)} {limit.unit}
              </span>
              <span
                className={cn(
                  "font-medium",
                  pct > 100
                    ? "text-destructive"
                    : pct > 80
                      ? "text-yellow-600"
                      : "text-muted-foreground",
                )}
              >
                {Math.round(pct)}%
              </span>
            </div>
          </>
        )}
      </button>

      {CPU_ROW_KEYS.has(limit.key) && (
        <div className="border-t px-3 py-1.5">
          <button
            type="button"
            onClick={scrollToCpuDrilldown}
            className="text-xs text-primary underline"
          >
            Per-Worker and per-route breakdown ↓
          </button>
        </div>
      )}

      {open && !limit.error && (
        <div className="border-t px-3 py-2 space-y-1">
          {limit.items.length === 0 ? (
            <p className="text-xs text-muted-foreground py-1">
              No activity in this window.
            </p>
          ) : (
            limit.items.map((item) => {
              // Only the R2 "Storage" row's items are real bucket names —
              // every other limit's breakdown is a database/script/org
              // name that has nothing analogous to "drill in and manage
              // contents", so this stays a plain row everywhere else.
              const canDrillIn = limit.key === "r2_storage" && onOpenBucket;
              const rowContent = (
                <>
                  <span className="truncate pr-2 text-foreground/80">
                    {item.name}
                    {canDrillIn && <span className="text-primary"> →</span>}
                  </span>
                  <span className="text-muted-foreground shrink-0">
                    {fmt(item.value)} {limit.unit}
                  </span>
                </>
              );
              return canDrillIn ? (
                <button
                  key={item.name}
                  type="button"
                  onClick={() => onOpenBucket(item.name)}
                  className="w-full flex items-baseline justify-between text-xs font-mono hover:bg-accent/40 rounded px-1 -mx-1"
                >
                  {rowContent}
                </button>
              ) : (
                <div
                  key={item.name}
                  className="flex items-baseline justify-between text-xs font-mono"
                >
                  {rowContent}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function ProductSection({
  product,
  onOpenBucket,
}: {
  product: UsageProduct;
  onOpenBucket: (bucket: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{product.label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {product.limits.map((limit) => (
          <LimitRow key={limit.key} limit={limit} onOpenBucket={onOpenBucket} />
        ))}
      </CardContent>
    </Card>
  );
}

export function UsagePage() {
  const token = useAuthToken();
  const call = useCallback(
    <T,>(path: string, init?: RequestInit) => api<T>(token, path, init),
    [token],
  );

  const [result, setResult] = useState<UsageResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openBucket, setOpenBucket] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    call<{ ok: true } & UsageResult>("/admin/usage")
      .then((r) => setResult(r))
      .catch((err) => setError(errMsg(err, "failed to load usage")))
      .finally(() => setLoading(false));
  }, [call]);

  useEffect(load, [load]);

  // Every limit already queries its own natural reset window (UTC day or
  // UTC calendar month) server-side — refresh often enough that a daily
  // row crossing 00:00 UTC, or a limit tipping over its threshold, shows
  // up without the operator needing to think about it.
  useEffect(() => {
    const t = setInterval(load, 5 * 60_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Usage</h1>
          <p className="text-sm text-muted-foreground">
            Cloudflare Free-plan usage across every product this account uses.
            Each limit is queried over the exact window Cloudflare resets it on
            — UTC calendar day for daily limits, UTC calendar month for monthly
            ones. Click a row to see what's contributing to it.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          className="shrink-0"
        >
          Refresh
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {result?.workersCpu && <CpuHitsBanner report={result.workersCpu} />}

      {result && (
        <>
          <p className="text-xs text-muted-foreground">
            Last updated{" "}
            {new Date(result.generated_at).toLocaleString(undefined, {
              timeZone: "UTC",
              timeZoneName: "short",
            })}
          </p>
          {result.products.map((product) => (
            <ProductSection
              key={product.key}
              product={product}
              onOpenBucket={setOpenBucket}
            />
          ))}
          {result.workersCpu && <CpuDrilldown report={result.workersCpu} />}
        </>
      )}

      {!result && loading && (
        <p className="text-sm text-muted-foreground">Loading usage…</p>
      )}

      {openBucket === "pure-image-registry" ? (
        <ImageRegistryBrowserDialog
          open={openBucket === "pure-image-registry"}
          onOpenChange={(o) => !o && setOpenBucket(null)}
          call={call}
        />
      ) : openBucket === "pure-registry-internal" ? (
        <RegistryInternalBrowserDialog
          open={openBucket === "pure-registry-internal"}
          onOpenChange={(o) => !o && setOpenBucket(null)}
          call={call}
        />
      ) : (
        <BucketBrowserDialog
          bucket={openBucket}
          onOpenChange={(o) => !o && setOpenBucket(null)}
          call={call}
        />
      )}
    </div>
  );
}
